import { useState, useEffect } from "react";
import { 
  Key, 
  Plus, 
  Copy, 
  Trash2, 
  Eye,
  EyeOff,
  Clock,
  CheckCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import api from "../services/api";
import { toast } from "sonner";

export default function ApiKeys() {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newKeyDialogOpen, setNewKeyDialogOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [newKey, setNewKey] = useState(null);
  const [keyName, setKeyName] = useState("");
  const [keyDescription, setKeyDescription] = useState("");
  const [showKeys, setShowKeys] = useState({});

  const fetchApiKeys = async () => {
    try {
      const res = await api.get("/api-keys");
      setApiKeys(res.data);
    } catch (error) {
      console.error("Error fetching API keys:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/api-keys", {
        name: keyName,
        description: keyDescription
      });
      setNewKey(res.data);
      setDialogOpen(false);
      setNewKeyDialogOpen(true);
      setKeyName("");
      setKeyDescription("");
      fetchApiKeys();
    } catch (error) {
      toast.error("Erro ao criar API Key");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api-keys/${selectedKey.id}`);
      toast.success("API Key excluída");
      setDeleteDialogOpen(false);
      setSelectedKey(null);
      fetchApiKeys();
    } catch (error) {
      toast.error("Erro ao excluir API Key");
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado para área de transferência");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Nunca";
    return new Date(dateStr).toLocaleString("pt-BR");
  };

  const toggleShowKey = (id) => {
    setShowKeys(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="api-keys-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono">API Keys</h1>
          <p className="text-sm text-zinc-500">Gerencie as chaves de acesso ao gateway</p>
        </div>
        <Button 
          onClick={() => setDialogOpen(true)}
          className="gap-2 bg-cyan-500 hover:bg-cyan-600 text-black font-medium btn-glow"
          data-testid="new-api-key-btn"
        >
          <Plus className="w-4 h-4" />
          Nova API Key
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-cyan-500/5 border-cyan-500/20">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Key className="w-5 h-5 text-cyan-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-cyan-400">Autenticação via API Key</p>
              <p className="text-xs text-zinc-400 mt-1">
                As API Keys são usadas para autenticar requisições ao gateway. 
                Inclua o header <code className="text-cyan-400">X-API-Key</code> em todas as requisições.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API Keys List */}
      <Card className="bg-zinc-900/50 border-zinc-800" data-testid="api-keys-list-card">
        <CardHeader>
          <CardTitle className="text-lg font-mono text-white flex items-center gap-2">
            <Key className="w-5 h-5 text-cyan-400" />
            Chaves Ativas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Key className="w-6 h-6 text-cyan-400 animate-pulse" />
            </div>
          ) : apiKeys.length > 0 ? (
            <div className="space-y-3">
              {apiKeys.map((apiKey) => (
                <div 
                  key={apiKey.id}
                  className="flex items-center gap-4 p-4 rounded-sm bg-zinc-800/30 hover:bg-zinc-800/50 transition-colors"
                  data-testid={`api-key-${apiKey.id}`}
                >
                  <div className="p-2 rounded-sm bg-zinc-800">
                    <Key className="w-5 h-5 text-cyan-400" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-white">{apiKey.name}</h3>
                      <Badge className={apiKey.is_active ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                        {apiKey.is_active ? "Ativa" : "Inativa"}
                      </Badge>
                    </div>
                    {apiKey.description && (
                      <p className="text-sm text-zinc-500 mt-1">{apiKey.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                      <span className="font-mono">{apiKey.key_prefix}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Criada: {formatDate(apiKey.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Último uso: {formatDate(apiKey.last_used)}
                      </span>
                    </div>
                  </div>

                  <Button 
                    variant="outline" 
                    size="icon"
                    className="text-red-400 hover:text-red-300 hover:border-red-400"
                    onClick={() => { setSelectedKey(apiKey); setDeleteDialogOpen(true); }}
                    data-testid={`delete-api-key-${apiKey.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
              <Key className="w-16 h-16 mb-4 opacity-50" />
              <p className="text-lg font-medium">Nenhuma API Key criada</p>
              <p className="text-sm">Crie uma chave para permitir acesso ao gateway</p>
              <Button 
                onClick={() => setDialogOpen(true)}
                className="mt-4 bg-cyan-500 hover:bg-cyan-600 text-black"
                data-testid="empty-new-api-key-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar API Key
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-mono text-white">Nova API Key</DialogTitle>
            <DialogDescription>
              Crie uma nova chave de acesso para o gateway
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="key_name">Nome da Chave</Label>
              <Input
                id="key_name"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                placeholder="Ex: RODOPESO Produção"
                className="bg-zinc-800 border-zinc-700"
                required
                data-testid="api-key-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="key_description">Descrição (opcional)</Label>
              <Input
                id="key_description"
                value={keyDescription}
                onChange={(e) => setKeyDescription(e.target.value)}
                placeholder="Descreva o propósito desta chave..."
                className="bg-zinc-800 border-zinc-700"
                data-testid="api-key-description-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-cyan-500 hover:bg-cyan-600 text-black"
                data-testid="create-api-key-btn"
              >
                Criar Chave
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Key Display Dialog */}
      <Dialog open={newKeyDialogOpen} onOpenChange={setNewKeyDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-mono text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              API Key Criada
            </DialogTitle>
            <DialogDescription>
              Copie a chave abaixo. Por segurança, ela não será exibida novamente.
            </DialogDescription>
          </DialogHeader>
          
          {newKey && (
            <div className="space-y-4">
              <div className="p-4 bg-zinc-800 rounded-sm">
                <Label className="text-zinc-500 text-xs">Sua nova API Key:</Label>
                <div className="flex items-center gap-2 mt-2">
                  <code className="flex-1 text-sm font-mono text-cyan-400 break-all">
                    {newKey.key}
                  </code>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => handleCopy(newKey.key)}
                    data-testid="copy-api-key-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                <p className="text-sm text-yellow-400">
                  ⚠️ Guarde esta chave em local seguro. Ela não poderá ser visualizada novamente.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              onClick={() => setNewKeyDialogOpen(false)}
              className="bg-cyan-500 hover:bg-cyan-600 text-black"
            >
              Entendi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir API Key</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a chave "{selectedKey?.name}"? 
              Qualquer sistema usando esta chave perderá acesso ao gateway.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-api-key-btn"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
